
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { download, post } from '@/services/http';
import { EventBus } from '@/eventBus';

@Component
export default class ImportCustomColumnValuesDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly dialog!: boolean;

  private importFile: File | null = null;
  private importing = false;
  private downloadingTemplate = false;
  private fileError = '';
  private importSummaryData: any = {};
  private showImportSummaryDialog = false;

  @Watch('dialog')
  onDialogChange(newVal: boolean) {
    if (newVal) {
      this.resetState();
    }
  }

  resetState() {
    this.importFile = null;
    this.fileError = '';
    this.importing = false;
    this.downloadingTemplate = false;
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  closeDialog() {
    this.resetState();
    this.importSummaryData = {};
    this.showImportSummaryDialog = false;
    this.$emit('update:dialog', false);
  }

  closeSummaryDialog() {
    this.showImportSummaryDialog = false;
    if (this.importSummaryData.failedRows === 0) {
      this.closeDialog();
      this.$emit('import-success');
    } else {
      this.resetState();
    }
  }

  async downloadTemplate() {
    this.downloadingTemplate = true;
    try {
      const blob = await download('/purchase_orders/config/custom-column-values/template');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'custom_column_values_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Download failed';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.downloadingTemplate = false;
    }
  }

  openFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement).click();
  }

  handleFileSelection(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      this.importFile = null;
      this.fileError = '';
      return;
    }

    const file = files[0];

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.fileError = 'Please select a valid CSV file.';
      this.importFile = null;
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.fileError = 'File size must be less than 5MB.';
      this.importFile = null;
      return;
    }

    this.importFile = file;
    this.fileError = '';
  }

  clearFileSelection() {
    this.importFile = null;
    this.fileError = '';
    if (this.$refs.csvFileInput) {
      (this.$refs.csvFileInput as HTMLInputElement).value = '';
    }
  }

  formatFileSize(size: number): string {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async importValues() {
    if (!this.importFile) return;

    this.importing = true;
    try {
      const formData = new FormData();
      formData.append('csvFile', this.importFile);

      const response: any = await post('/purchase_orders/config/custom-column-values/import', formData, true);

      this.importSummaryData = response.data || response;
      this.showImportSummaryDialog = true;

    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'An unknown error occurred.';
      this.fileError = `Import failed: ${message}`;
      if (error.response?.data?.errorDetails) {
        this.importSummaryData = error.response.data;
        this.showImportSummaryDialog = true;
      } else {
        EventBus.$emit('show-snackbar', { text: this.fileError, color: 'error' });
      }
    } finally {
      this.importing = false;
    }
  }
}
